import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  TextInput,
  Button,
  SelectableButtons,
  DynamicDropdown,
  DynamicMultiSelectDropdown,
  StaticDropdown,
} from "src/Components";
// Hooks
import { useAppSelector } from "src/App/hooks";
import {
  getRollFilterSetting,
  setRollFilterSetting,
} from "src/Requests/coveredCall";
// Request
import {
  getBaseInstruments,
  getOptionInstruments,
} from "src/Requests/coveredCall";
// Types
import { IRollFilterParams } from "src/Interfaces/coveredCall";
import { ExchangeType } from "src/Interfaces/connections";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface IProps {
  position: "up" | "down" | "emergency";
  onClose: () => void;
  onApplied?: () => void;
}

const exchangeTypeOption: DropdownItem[] = [
  { id: ExchangeType.StockExchange, name: "اوراق بهادار" },
  { id: ExchangeType.CommodityExchange, name: "کالا" },
];

const FilterSettingModal: React.FC<IProps> = ({
  position,
  onClose,
  onApplied,
}) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // state
  const [loading, setLoading] = React.useState(false);
  const [riskProfileId, setRiskProfileIds] = React.useState<string>(
    riskProfiles[1]?.id?.toString() ?? "0",
  );
  const [searchParam, setSearchParam] = React.useState<IRollFilterParams>(
    {} as IRollFilterParams,
  );

  React.useEffect(() => {
    getRollFilterSetting(position, riskProfileId)
      .then((resp) => {
        setSearchParam(resp);
      })
      .catch(packetErrorHandler);
  }, [riskProfileId, position]);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    setLoading(true);
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setRollFilterSetting(
      cleanObject as IRollFilterParams,
      riskProfileId,
      position,
    )
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          if (onApplied) onApplied();
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full !min-w-full">
      <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full !min-w-full">
        <SelectableButtons
          label="ریسک پروفایل"
          name="riskProfileId"
          onChange={(_: string, value: string) => setRiskProfileIds(value)}
          options={riskProfiles}
          value={riskProfileId}
          hideSelectAll
        />
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "baseInstrumentIds",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={getBaseInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseInstrumentIds}
            />
          </div>
          <div className="w-full">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              onChange={searchParamChangeHandler}
              options={exchangeTypeOption}
              value={searchParam?.exchangeType?.toString() || ""}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "sourceInstrumentId",
                placeholder: "مبدا رول",
                extraLabel: true,
              }}
              getData={getOptionInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.sourceInstrumentId}
            />
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "destInstrumentId",
                placeholder: "مقصد رول",
                extraLabel: true,
              }}
              getData={getOptionInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.destInstrumentId}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            <TextInput
              name="minProfitPercentage"
              label="حداقل % بازده روزانه رول"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minProfitPercentage?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minYesterdayVolume"
              label="حداقل حجم معاملات روز قبل مقصد"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayVolume?.toString() || ""}
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="minYesterdayValue"
              label="حداقل ارزش معاملات روز قبل مقصد"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayValue?.toString() || ""}
              kind="number"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minSourceDTM"
              label="حداقل DTM مبدا"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minSourceDTM?.toString() || ""}
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxSourceDTM"
              label="حداکثر DTM مبدا"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxSourceDTM?.toString() || ""}
              kind="number"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minDestinationDTM"
              label="حداقل DTM مقصد"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minDestinationDTM?.toString() || ""}
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxDestinationDTM"
              label="حداکثر DTM مقصد"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxDestinationDTM?.toString() || ""}
              kind="number"
            />
          </div>
        </div>
      </div>
      <div className="w-64 mt-3 self-center">
        <Button
          onClick={handleSearch}
          text="ثبت"
          variant="contained"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default FilterSettingModal;
